/**
 * Shared control styling for the signup forms (spec sections 11 and 18).
 *
 * Three constraints are baked in here rather than left to each field:
 *
 * - **44px minimum target.** `min-h-12` is 48px, comfortably over it, and the
 *   same height on every control so a thumb has one target size to learn.
 * - **16px text.** `text-base` keeps iOS Safari from zooming the page when an
 *   input takes focus, which is the most common cause of an apparently broken
 *   mobile form.
 * - **Visible focus.** A 2px outline offset from the control, in ink, on every
 *   focusable thing. Never removed.
 *
 * The invalid state is a heavier border *and* a message. Colour never carries
 * the meaning on its own (spec section 18).
 *
 * There is no placeholder colour here because no control has a placeholder.
 * Every field is labelled by a real `<label for>` (components/forms/Field.tsx)
 * and the forms tests assert the absence of the attribute. A dimmed placeholder
 * colour sitting unused is a contrast failure waiting for the first person who
 * adds one, so it is not left lying around.
 *
 * Text opacity floor across the site is 70%: 70% ink is 4.67:1 on the warm
 * background and 4.96:1 on the field background, both over the 4.5:1 AA
 * threshold for body text. Anything lighter fails, which axe will say so
 * plainly (tests/e2e/a11y.spec.ts). The one exception below is the *disabled*
 * state, which WCAG 1.4.3 exempts and which needs to read as unavailable — the
 * only control that is ever disabled here is the category select, and only for
 * the moment before its options arrive.
 */

const base =
  'block w-full min-h-12 rounded-md border-2 bg-field px-4 py-3 text-base text-ink ' +
  'transition-colors ' +
  'focus:border-ink focus:outline-2 focus:outline-offset-2 focus:outline-ink ' +
  'disabled:cursor-not-allowed disabled:bg-warm disabled:text-ink/60';

/** Neutral and invalid border treatments. */
const borders = {
  normal: 'border-ink/30 hover:border-ink/50',
  invalid: 'border-signal',
} as const;

export function controlClasses(invalid: boolean): string {
  return `${base} ${invalid ? borders.invalid : borders.normal}`;
}

/** Textareas need a comfortable default height; everything else matches. */
export function textAreaClasses(invalid: boolean): string {
  return `${controlClasses(invalid)} min-h-32 resize-y`;
}

/**
 * One checkbox row, used both by the additional-services group and by the
 * standalone "I have horses of my own" row on the provider form. Shared so the
 * lone checkbox cannot drift away from the group above it: same 48px row, same
 * checked treatment, same focus ring drawn on the row rather than on the box.
 */
export function checkboxRowClasses(checked: boolean, invalid = false): string {
  return [
    'flex min-h-12 cursor-pointer items-center gap-3 rounded-md border-2 px-3 py-2',
    'text-sm leading-snug text-ink transition-colors',
    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ink',
    checked ? 'border-ink bg-warm font-semibold' : 'border-ink/30 bg-field hover:border-ink/50',
    invalid ? 'border-signal' : '',
  ].join(' ');
}

/** The box itself. Sized to sit centred in a `checkboxRowClasses` row. */
export const checkboxInputClasses = 'h-5 w-5 shrink-0 accent-ink';

export const labelClasses = 'block text-sm font-semibold text-ink';
export const hintClasses = 'mt-1 text-sm leading-relaxed text-ink/75';
export const errorClasses = 'mt-2 text-sm font-semibold leading-relaxed text-signal';
export const optionalClasses = 'ml-2 text-sm font-normal text-ink/70';
